import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ClockIcon, 
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const BusinessHours = ({ settings, onSettingsChange, onHasChanges }) => {
  const [businessHours, setBusinessHours] = useState({
    monday: { open: '06:00', close: '22:00', closed: false },
    tuesday: { open: '06:00', close: '22:00', closed: false },
    wednesday: { open: '06:00', close: '22:00', closed: false },
    thursday: { open: '06:00', close: '22:00', closed: false },
    friday: { open: '14:00', close: '22:00', closed: false },
    saturday: { open: '08:00', close: '20:00', closed: false },
    sunday: { open: '08:00', close: '18:00', closed: false }
  });

  useEffect(() => {
    if (settings?.businessHours) {
      setBusinessHours(settings.businessHours);
    }
  }, [settings]);

  const handleDayChange = (day, field, value) => {
    const newBusinessHours = {
      ...businessHours,
      [day]: {
        ...businessHours[day],
        [field]: value
      }
    };
    setBusinessHours(newBusinessHours);
    onSettingsChange({ ...settings, businessHours: newBusinessHours });
    onHasChanges(true);
  };

  const handleCopyToAll = (day) => {
    const daySchedule = businessHours[day];
    const newBusinessHours = {};
    
    Object.keys(businessHours).forEach(d => {
      newBusinessHours[d] = { ...daySchedule };
    });
    
    setBusinessHours(newBusinessHours);
    onSettingsChange({ ...settings, businessHours: newBusinessHours });
    onHasChanges(true);
  };

  const days = [
    { key: 'monday', label: 'Lundi', icon: '🌟' },
    { key: 'tuesday', label: 'Mardi', icon: '💪' },
    { key: 'wednesday', label: 'Mercredi', icon: '🔥' },
    { key: 'thursday', label: 'Jeudi', icon: '💯' },
    { key: 'friday', label: 'Vendredi', icon: '🕌' },
    { key: 'saturday', label: 'Samedi', icon: '🏋️' },
    { key: 'sunday', label: 'Dimanche', icon: '🌅' }
  ];

  const getStatusColor = (day) => {
    const daySchedule = businessHours[day];
    if (daySchedule.closed) return 'text-red-400';
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const openTime = parseInt(daySchedule.open.split(':')[0]) * 60 + parseInt(daySchedule.open.split(':')[1]);
    const closeTime = parseInt(daySchedule.close.split(':')[0]) * 60 + parseInt(daySchedule.close.split(':')[1]);
    
    const currentDay = now.getDay();
    const dayIndex = days.findIndex(d => d.key === day);
    const adjustedDayIndex = dayIndex === 6 ? 0 : dayIndex + 1; // Adjust for Sunday = 0
    
    if (currentDay === adjustedDayIndex) {
      if (currentTime >= openTime && currentTime <= closeTime) {
        return 'text-green-400';
      } else {
        return 'text-orange-400';
      }
    }
    
    return 'text-slate-600 dark:text-slate-400';
  };

  const getStatusText = (day) => {
    const daySchedule = businessHours[day];
    if (daySchedule.closed) return 'Fermé';
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const openTime = parseInt(daySchedule.open.split(':')[0]) * 60 + parseInt(daySchedule.open.split(':')[1]);
    const closeTime = parseInt(daySchedule.close.split(':')[0]) * 60 + parseInt(daySchedule.close.split(':')[1]);
    
    const currentDay = now.getDay();
    const dayIndex = days.findIndex(d => d.key === day);
    const adjustedDayIndex = dayIndex === 6 ? 0 : dayIndex + 1;
    
    if (currentDay === adjustedDayIndex) {
      if (currentTime >= openTime && currentTime <= closeTime) {
        return 'Ouvert maintenant';
      } else if (currentTime < openTime) {
        return 'Ouvre bientôt';
      } else {
        return 'Fermé pour aujourd\'hui';
      }
    }
    
    return `${daySchedule.open} - ${daySchedule.close}`;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {days.map((day, index) => {
          const daySchedule = businessHours[day.key];
          return (
            <motion.div
              key={day.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6 ${
                daySchedule.closed ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{day.icon}</span>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-slate-50">{day.label}</h4>
                    <p className={`text-sm ${getStatusColor(day.key)}`}>
                      {getStatusText(day.key)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleCopyToAll(day.key)}
                    className="text-xs bg-blue-500/20 text-blue-300 px-3 py-1 rounded-lg hover:bg-blue-500/30 transition-colors"
                  >
                    Copier à tous
                  </button>
                  <button
                    onClick={() => handleDayChange(day.key, 'closed', !daySchedule.closed)}
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs transition-colors ${
                      daySchedule.closed 
                        ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' 
                        : 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                    }`}
                  >
                    {daySchedule.closed ? (
                      <>
                        <XMarkIcon className="h-3 w-3" />
                        Fermé
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-3 w-3" />
                        Ouvert
                      </>
                    )}
                  </button>
                </div>
              </div>

              {!daySchedule.closed && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                      Heure d'ouverture
                    </label>
                    <input
                      type="time"
                      value={daySchedule.open}
                      onChange={(e) => handleDayChange(day.key, 'open', e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                      Heure de fermeture
                    </label>
                    <input
                      type="time"
                      value={daySchedule.close}
                      onChange={(e) => handleDayChange(day.key, 'close', e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {daySchedule.closed && (
                <div className="flex items-center gap-2 text-red-300 bg-red-500/10 rounded-xl p-3">
                  <ExclamationCircleIcon className="h-4 w-4" />
                  <span className="text-sm">Salle fermée ce jour</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Résumé des horaires */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">Résumé des horaires</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {days.map(day => {
            const daySchedule = businessHours[day.key];
            return (
              <div key={day.key} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{day.icon}</span>
                  <span className="text-slate-700 dark:text-slate-300">{day.label}</span>
                </div>
                <div className={`text-sm ${getStatusColor(day.key)}`}>
                  {daySchedule.closed ? 'Fermé' : `${daySchedule.open} - ${daySchedule.close}`}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default BusinessHours; 